import axios from 'axios'

// Get API base URL from environment or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Create axios instance
export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null // This makes arrays serialize as source_ids=1&source_ids=2 instead of source_ids[]=1
  }
})

// Auth token management
const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'

export const authService = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    const user = localStorage.getItem(USER_KEY)
    return user ? JSON.parse(user) : null
  },
  setAuth: (token, username) => {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify({ username }))
  },
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
  
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password })
    const { access_token, username: user } = response.data
    authService.setAuth(access_token, user)
    return response.data
  },
  
  logout: () => {
    authService.clearAuth()
  }
}

// Request interceptor - add auth header if token exists
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle 401 - clear auth and let the UI handle it
    if (error.response?.status === 401) {
      authService.clearAuth()
    }
    
    // Handle common errors
    if (error.response?.status === 404) {
      console.warn('Resource not found:', error.config?.url)
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.status, error.response?.data)
    } else if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNREFUSED') {
      console.error('Network error - API server may be down')
    }
    
    return Promise.reject(error)
  }
)

// API methods
export const articlesApi = {
  getAll: (params = {}) => api.get('/articles/', { params }),
  getById: (id) => api.get(`/articles/${id}/`),
  search: (query, params = {}) => api.get(`/articles/search/${encodeURIComponent(query)}/`, { params }),
  updateScore: (id, score) => api.patch(`/articles/${id}/score`, { human_score: score })
}

export const sourcesApi = {
  getAll: (activeOnly = true) => api.get('/sources/', { params: { active_only: activeOnly } }),
  getById: (id) => api.get(`/sources/${id}/`),
  update: (id, data) => api.patch(`/sources/${id}/`, data),
  toggle: (id) => api.post(`/sources/${id}/toggle/`)
}

export default api
